import i18next from "i18next";
import ChainedBackend from "i18next-chained-backend";
import HttpApi from "i18next-http-backend";
import Vue from "vue";
import I18NextVue from "i18next-vue";
import Locize from "i18next-locize-backend";
import dayjs from "@/plugins/dayjs";
import LanguageDetector from "i18next-browser-languagedetector";
import * as Sentry from "@sentry/vue";
import sanitizeHtml from "sanitize-html";

let configBackend;

if (process.env.VUE_APP_LOCIZE_SELF_HOSTED === "true") {
  configBackend = {
    backend: HttpApi,
    config: {
      loadPath: "/i18n/{{lng}}/{{ns}}.json"
    }
  };
} else {
  configBackend = {
    backend: Locize,
    config: {
      projectId: process.env.VUE_APP_LOCIZE_PROJECT_ID,
      apiKey: process.env.VUE_APP_LOCIZE_API_KEY,
      referenceLng: "dev",
      version: process.env.VUE_APP_LOCIZE_VERSION || "latest",
      // private - set to true if you version on locize is set to use private publish
      private: false,
      // hostnames that are allowed to create, update keys
      // please keep those to your local system, staging, test servers (not production)
      allowedAddOrUpdateHosts: [] // no
    }
  };
}

const ns = [
  "common",
  "calendar",
  "events",
  "horses",
  "navigation",
  "organisations",
  "people",
  "server",
  "signin",
  "support",
  "temporary",
  "wallet",
  "rankings",
  "shop",
  "survey",
  "sanctions"
];

if (process.env.VUE_APP_COUNTRY === "FI") {
  ns.push("historyfi");
}

const detection = {
  order: ["querystring", "localStorage", "htmlTag"],
  lookupQuerystring: "language",
  lookupLocalStorage: "lang",
  caches: ["localStorage"], // cache user language on
  excludeCacheFor: ["cimode"], // languages to not persist (localStorage)
  convertDetectedLanguage: lng => {
    if (lng.includes("_")) {
      lng = lng.split("_").join("-");
    }
    if (process.env.VUE_APP_COUNTRY == "BE") {
      lng = lng.split("-")[0] + "-BE";
    }

    return lng;
  }
};

if (process.env.VUE_APP_COUNTRY === "BE") {
  detection.order.splice(2, 0, "navigator");
}

export function loadUpTranslations() {
  return i18next
    .use(LanguageDetector)
    .use(ChainedBackend)
    .init({
      detection,
      debug: false,
      fallbackLng: {
        en: ["dev"],
        "en-NL": ["en", "dev"],
        "nl-NL": ["en-NL", "en", "dev"],
        "en-FI": ["en", "dev"],
        "en-NO": ["en", "dev"],
        "en-UA": ["en", "dev"],
        fi: ["en-FI", "en", "dev"],
        et: ["en", "dev"],
        "nb-NO": ["en-NO", "en", "dev"],
        "sv-FI": ["en-FI", "en", "dev"],
        uk: ["en-UA", "en", "dev"],

        "en-BE": ["en", "dev"],
        "fr-BE": ["en-BE", "en", "dev"],
        "nl-BE": ["en-BE", "en", "dev"],

        default: ["en", "dev"]
      },
      compatibilityJSON: "v3",
      load: "currentOnly",
      ns,
      defaultNS: "common",
      fallbackNS: "common",
      backend: {
        backends: [configBackend.backend],
        backendOptions: [configBackend.config]
      },
      interpolation: {
        escape: function (v) {
          return sanitizeHtml(v, {
            allowedTags: [
              "address",
              "article",
              "aside",
              "h1",
              "h2",
              "h3",
              "h4",
              "h5",
              "h6",
              "hgroup",
              "blockquote",
              "dd",
              "div",
              "dl",
              "dt",
              "figcaption",
              "figure",
              "hr",
              "li",
              "ol",
              "p",
              "pre",
              "ul",
              "a",
              "abbr",
              "b",
              "bdi",
              "bdo",
              "br",
              "cite",
              "code",
              "data",
              "dfn",
              "em",
              "i",
              "kbd",
              "mark",
              "q",
              "rb",
              "rp",
              "rt",
              "rtc",
              "ruby",
              "s",
              "samp",
              "small",
              "span",
              "strong",
              "sub",
              "sup",
              "time",
              "u",
              "var",
              "wbr",
              "caption"
            ],
            nonBooleanAttributes: ["style"],
            allowedAttributes: {
              "*": ["style"],
              a: ["href", "name", "target", "rel", "style"]
            },
            selfClosing: ["br", "hr"]
          });
        },
        format: function (value, format, lng) {
          if (format == "date") return dayjs(value).format(format);
          return value;
        }
      },
      editor: {
        onEditorSaved: function (lng, ns) {
          i18next.reloadResources(lng, ns, () => {
            // trigger rerender on your app if needed
            // note: reloadResources can take an optional callback in i18next@>=11.9.0
          });
        }
      },
      saveMissing: process.env.VUE_APP_LOCIZE_REPORT_MISSING_KEYS === "true", // so missingKeyHandler can run
      saveMissingTo: "dev",
      missingKeyHandler: function (lngs, namespace, key, fallbackValue) {
        //console.log(lngs, namespace, key, res);
        const currentLanguage = i18next.currentLanguage;
        // lngs only includes languages that do not have the key
        if (!lngs.includes(currentLanguage)) return;
        Sentry.withScope(scope => {
          scope.setLevel("info");
          scope.setTag("language_missing_keys", currentLanguage);
          scope.setContext("Missing key details", {
            lngs,
            namespace,
            key,
            currentLanguage,
            fallbackValue
          });
          Sentry.captureMessage(
            `${namespace}:${key} is missing in ${currentLanguage}`
          );
        });
      }
    });
}

Vue.use(I18NextVue, { i18next });

export default i18next;

export function useTranslation() {
  return {
    t: Vue.prototype.$t
  };
}
